import axios from 'axios';
import { API_BASE_URL, AUTH_BASE_URL } from '../config';
import { getToken, getRefreshToken, setToken, removeToken } from "@/utils/tokenStorage";
import { authService } from './authService';
import useUserStore from '@/store/userStore';

// Create API clients
const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

const authClient = axios.create({
  baseURL: AUTH_BASE_URL,
});

// Request interceptor to add token for auth client
authClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token && !config.headers?.Authorization) {
      config.headers.Authorization = `${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Request interceptor to add token for API client
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token && !config.headers?.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Response interceptor for token refresh
const createResponseInterceptor = (client) => {
  return client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }
        
        originalRequest._retry = true;
        isRefreshing = true;
        
        const refreshToken = getRefreshToken();
        
        if (refreshToken) {
          try {
            const response = await authService.refreshToken(refreshToken);
            
            if (response.success && response.data?.access_token) {
              const newToken = response.data.access_token;
              setToken(newToken);
              
              // Update user store
              const currentUser = useUserStore.getState().user;
              useUserStore.getState().login(currentUser, newToken);
              
              processQueue(null, newToken);
              
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return client(originalRequest);
            }
          } catch (refreshError) {
            processQueue(refreshError, null);
            
            // Refresh failed, logout user
            useUserStore.getState().logout();
            if (!window.location.pathname.startsWith('/auth/')) {
              window.location.href = '/auth/login';
            }
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        } else {
          isRefreshing = false;
          processQueue(error, null);
          useUserStore.getState().logout();
          if (!window.location.pathname.startsWith('/auth/')) {
            window.location.href = '/auth/login';
          }
          return Promise.reject(error);
        }
      }
      
      return Promise.reject(error);
    }
  );
};

// Apply interceptors
createResponseInterceptor(apiClient);
createResponseInterceptor(authClient);

export { apiClient, authClient };